// src/views/EditCustomFees.jsx
import React, { useEffect, useState } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync } from 'react-icons/fa';
import './Invoices.css';

const API = import.meta.env.VITE_API_URL;

export default function EditCustomFees() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/templates/customfees`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addRow = () => setRows(prev => [{ id: null, name: '', description: '', amt: 0, _new: true }, ...prev]);

  const saveRow = async (row) => {
    setSavingId(row.id || 'new');
    try {
      const payload = {
        name: row.name,
        description: row.description,
        amt: Number(row.amt || 0)
      };
      if (!row.id) {
        const res = await fetch(`${API}/api/templates/customfees`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const json = await res.json();
        if (!res.ok || json?.error) throw new Error(json?.error || 'Create failed');
        setRows(prev => prev.map(r => (r === row ? { ...json, _new: undefined } : r)));
      } else {
        const res = await fetch(`${API}/api/templates/customfees/${row.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.error) throw new Error(json?.error || 'Save failed');
        setRows(prev => prev.map(r => (r.id === row.id ? { ...row } : r)));
      }
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!row.id) { setRows(prev => prev.filter(x => x !== row)); return; }
    if (!window.confirm('Delete this template fee?')) return;
    try {
      const res = await fetch(`${API}/api/templates/customfees/${row.id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.error) throw new Error(json?.error || 'Delete failed');
      setRows(prev => prev.filter(x => x.id !== row.id));
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  return (
    <div className="invoices-wrapper" style={{ gap: 12, width: '65%' }}>
      <div className="invoice-filters" style={{ justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>Edit Custom Fees</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={addRow}><FaPlus style={{ marginRight: 6 }} /> Add</button>
          <button className="btn" onClick={load}><FaSync style={{ marginRight: 6 }} /> Refresh</button>
        </div>
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <div className="invoice-table-wrapper">
          <table className="invoice-table shrink-cols">
            <thead>
              <tr>
                <th className="actions-col">Actions</th>
                <th className="fit-col">Fee Name</th>
                <th className="fit-col">Amount</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row, idx) => (
                <tr key={row.id || `n-${idx}`}>
                  <td className="actions-cell">
                    <button className="mini-btn" onClick={() => saveRow(row)} disabled={savingId != null} title="Save">
                      <FaSave size={16} />
                    </button>
                    <button className="mini-btn danger" onClick={() => deleteRow(row)} title="Delete">
                      <FaTrash size={16} />
                    </button>
                  </td>
                  <td className="fit-col">
                    <input
                      value={row.name || ''}
                      onChange={e => setRows(prev => prev.map(r => (r === row ? { ...r, name: e.target.value } : r)))}
                    />
                  </td>
                  <td className="fit-col">
                    <input
                      type="number"
                      step="0.01"
                      value={row.amt ?? ''}
                      onChange={e => setRows(prev => prev.map(r => (r === row ? { ...r, amt: e.target.value } : r)))}
                      style={{ width: '8ch' }}
                    />
                  </td>
                  <td>
                    <input
                      value={row.description || ''}
                      onChange={e => setRows(prev => prev.map(r => (r === row ? { ...r, description: e.target.value } : r)))}
                    />
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={4}>No custom fee templates</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
